import { Grid, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { ITeam, updateTeams, saveUser, IUserDTO } from 'wisestep-api';
import {
  Item,
  WsSelect,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsToggleButton,
  WsSelectObj,
  WsButton,
  Spinner,
} from 'wisestep-base';

import { SettingsHeader } from '../../../../components/SettingsHeader';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { useUserContext } from '../../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../../features/teams/helper/defaultValues';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { initializeTeamSettings } from '../../util/TeamSettingsHelper';
import { getTimezones } from '../../util/TimezoneHelper';
import WsSmtpSetup from '../../WsSmtpSetup';
const EmailPreferences = () => {
  const [timezones, setTimezones] = useState<WsSelectObj[]>([]);
  const [teamsData, setTeamsData] = useState<ITeam[]>([]);
  const [loadPage, setLoadPage] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext();
  useEffect(() => {
    (async function () {
      const teamsModified = await initializeTeamSettings();
      if (teamsModified) setTeamsData(teamsModified);
    })();
  }, []);
  const methods = useForm();
  const { watch } = methods;
  useEffect(() => {
    const timezoneArray: WsSelectObj[] = getTimezones().map((val) => ({
      label: val.label,
      value: val.tzCode,
    }));
    setTimezones(timezoneArray);
  }, [teamsData]);
  const configuredTeamsData = useMemo(
    () => teamsData?.map((teamDTOList) => teamDTOList),
    [teamsData]
  );
  const timezoneUpdater = watch('timezone');
  useEffect(() => {
    (async function () {
      if (timezoneUpdater != null) {
        const payload = loggedInUser?.userDTO;
        if (payload) {
          payload.timezone = timezoneUpdater;
          try {
            await saveUser({ ...(payload as IUserDTO) });
            setNotification(success('Timezone Updated Successfully.'));
          } catch (error) {
            setNotification(warning('Something Went Wrong , Please try again'));
          }
        }
      }
    })();
    setLoadPage(true);
  }, [loggedInUser?.userDTO, setNotification, timezoneUpdater]);
  const updateAnalyticsEmailFrequency = async (frequency: string) => {
    if (loggedInUser?.userDTO != null) {
      const payload = loggedInUser?.userDTO;
      if (!payload.analyticsEmailFrequency) {
        payload.analyticsEmailFrequency = [];
      }
      const idx = payload?.analyticsEmailFrequency?.indexOf(frequency);
      if (idx != null) {
        if (idx === -1) {
          payload?.analyticsEmailFrequency?.push(frequency);
        } else {
          payload?.analyticsEmailFrequency?.splice(idx, 1);
        }
      }
      try {
        await saveUser({ ...(payload as IUserDTO) });
        setNotification(success('Analytics Settings Updated Successfully.'));
        window.location.reload();
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const buttonList = [
    {
      label: 'Subscribe',
      value: 'ONE',
    },
    {
      label: 'Un-Subscribe',
      value: 'TWO',
    },
  ];
  const updateCommPreference = async (team: ITeam, key) => {
    let values;

    if (!team[key]) {
      values = [];
    } else {
      values = team[key].split(',');
    }
    const indexOfRecruiter = values.indexOf(loggedInUser?.userDTO?.email);
    if (indexOfRecruiter === -1) {
      values.push(loggedInUser?.userDTO?.email);
    } else {
      values.splice(indexOfRecruiter, 1);
    }
    team[key] = values.join(',');
    if (!isEmpty(team)) {
      try {
        await updateTeams({ ...team });
        setNotification(success('Communication Settings Updated Successfully.'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const showDefaultCommPreference = (lists: ITeam, key) => {
    if (
      !lists?.[key] ||
      lists?.[key].split(',').indexOf(loggedInUser?.userDTO?.email ?? '') === -1
    ) {
      return 'ONE';
    } else {
      return 'TWO';
    }
  };

  return (
    <>
      {loadPage ? (
        <Box margin={1}>
          <WsBreadCrumbs
            breadCrumbsList={getSettingBreadCrumbList(
              ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
              Pages['EMAIL_SETTINGS']
            )}
          />
          <SettingsHeader title="Email Setttings" />
          <Grid marginTop={1} container spacing={2}>
            <Grid item xs={2.5} pl={0} borderRadius={1.5}>
              <WsSmtpSetup />
            </Grid>
            <Grid item xs={9.5}>
              <Item>
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                  Analytics Email
                </WsTypography>
              </Item>
              <Grid container spacing={2}>
                <Grid item xs={3} md={6} borderRadius={1.5}>
                  <Item>
                    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                      Timezone
                    </WsTypography>
                  </Item>
                  <Item>
                    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                      Frequency
                    </WsTypography>
                  </Item>
                </Grid>
                <Grid item xs={3} md={6} borderRadius={1.5}>
                  <Stack justifyContent={'space-evenly'} spacing={1} alignItems={'flex-start'}>
                    <FormProvider {...methods}>
                      <WsSelect
                        name={'timezone'}
                        label={'Timezone'}
                        defaultValue={
                          (loggedInUser?.userDTO?.timezone && loggedInUser?.userDTO?.timezone) ?? ''
                        }
                        selectArray={timezones}
                      />
                    </FormProvider>
                    <Stack direction="row" spacing={1}>
                      <WsButton
                        variant={
                          loggedInUser?.userDTO?.analyticsEmailFrequency?.indexOf('DAILY_EMAIL') !=
                          -1
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => updateAnalyticsEmailFrequency('DAILY_EMAIL')}
                      >
                        Daily
                      </WsButton>
                      <WsButton
                        variant={
                          loggedInUser?.userDTO?.analyticsEmailFrequency?.indexOf('WEEKLY_EMAIL') !=
                          -1
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => updateAnalyticsEmailFrequency('WEEKLY_EMAIL')}
                      >
                        Weekly
                      </WsButton>
                      <WsButton
                        variant={
                          loggedInUser?.userDTO?.analyticsEmailFrequency?.indexOf(
                            'MONTHLY_EMAIL'
                          ) != -1
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => updateAnalyticsEmailFrequency('MONTHLY_EMAIL')}
                      >
                        Monthly
                      </WsButton>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
              {configuredTeamsData.map((lists) => (
                <Box key={lists.id} marginTop={2}>
                  <Item>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      {lists.name ?? ''}
                    </WsTypography>
                  </Item>
                  <Grid container spacing={2}>
                    <Grid item xs={3} md={6} borderRadius={1.5}>
                      <Stack justifyContent={'space-between'} alignItems={'flex-start'}>
                        <Item>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                            When a Job is Shared
                          </WsTypography>
                        </Item>
                        <Item>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                            When a Job is Closed
                          </WsTypography>
                        </Item>
                        <Item>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                            Job insights update
                          </WsTypography>
                        </Item>
                        <Item>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                            When a Candidate is Submitted
                          </WsTypography>
                        </Item>
                        <Item>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                            When a Candidate Status is Submitted
                          </WsTypography>
                        </Item>
                      </Stack>
                    </Grid>
                    <Grid item xs={3} md={6} borderRadius={1.5}>
                      <Stack
                        justifyContent={'space-evenly'}
                        spacing={3.6}
                        alignItems={'flex-start'}
                      >
                        <WsToggleButton
                          buttonsList={buttonList}
                          defaultValue={showDefaultCommPreference(
                            lists,
                            'disableJobShareNotificationTo'
                          )}
                          selectedIndexValue={() =>
                            updateCommPreference(lists, 'disableJobShareNotificationTo')
                          }
                        ></WsToggleButton>
                        <WsToggleButton
                          buttonsList={buttonList}
                          defaultValue={showDefaultCommPreference(
                            lists,
                            'disableJobClosedNotificationTo'
                          )}
                          selectedIndexValue={() =>
                            updateCommPreference(lists, 'disableJobClosedNotificationTo')
                          }
                        ></WsToggleButton>
                        <WsToggleButton
                          buttonsList={buttonList}
                          defaultValue={showDefaultCommPreference(
                            lists,
                            'disableJobInsightUpdateNotificationTo'
                          )}
                          selectedIndexValue={() =>
                            updateCommPreference(lists, 'disableJobInsightUpdateNotificationTo')
                          }
                        ></WsToggleButton>
                        <WsToggleButton
                          buttonsList={buttonList}
                          defaultValue={showDefaultCommPreference(
                            lists,
                            'disableCandidateSubmissionNotificationTo'
                          )}
                          selectedIndexValue={() =>
                            updateCommPreference(lists, 'disableCandidateSubmissionNotificationTo')
                          }
                        ></WsToggleButton>
                        <WsToggleButton
                          buttonsList={buttonList}
                          defaultValue={showDefaultCommPreference(
                            lists,
                            'disableCandidateStatusUpdateNotificationTo'
                          )}
                          selectedIndexValue={() =>
                            updateCommPreference(
                              lists,
                              'disableCandidateStatusUpdateNotificationTo'
                            )
                          }
                        ></WsToggleButton>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Spinner />
      )}
    </>
  );
};
export default EmailPreferences;
